import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
    Trophy, ArrowLeft, RefreshCw, Users, Calendar,
    ChevronRight, Crown, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TournamentBracketPage = () => {
    const { tournamentId } = useParams();
    const { token } = useAuth();
    const [bracket, setBracket] = useState(null);
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchBracket();
    }, [tournamentId]);

    const fetchBracket = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/matches/tournament/${tournamentId}/bracket`);
            setBracket(response.data);
            setTournament({
                name: response.data.tournament_name,
                sport: response.data.sport
            });
        } catch (error) {
            console.error('Failed to fetch bracket:', error);
            toast.error('Failed to load tournament bracket');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateBracket = async () => {
        try {
            const response = await axios.post(
                `${API_URL}/api/matches/tournament/${tournamentId}/generate-bracket`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Generated ${response.data.matches.length} matches!`);
            fetchBracket();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate bracket');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628]">
            {/* Header */}
            <div className="bg-[#0d2847] border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to={`/tournaments/${tournamentId}`} className="text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-5 h-5" />
                            </Link>
                            <div>
                                <h1 className="font-heading font-bold text-2xl uppercase tracking-tight text-white">
                                    Tournament Bracket
                                </h1>
                                <p className="text-sm text-muted-foreground">
                                    {tournament?.name} - {tournament?.sport}
                                </p>
                            </div>
                        </div>
                        {token && (
                            <Button 
                                onClick={handleGenerateBracket}
                                className="bg-primary"
                                data-testid="generate-bracket-btn"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Generate Bracket
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Bracket Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {!bracket || bracket.matches?.length === 0 ? (
                    <div className="text-center py-16 bg-[#0d2847] border border-white/5">
                        <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-white mb-2">No Bracket Yet</h2>
                        <p className="text-muted-foreground mb-4">
                            The tournament bracket hasn't been generated yet.
                        </p>
                        {token && (
                            <Button onClick={handleGenerateBracket} className="bg-primary">
                                Generate Bracket
                            </Button>
                        )}
                    </div>
                ) : (
                    <BracketDisplay bracket={bracket} />
                )}
            </div>
        </div>
    );
};

const BracketDisplay = ({ bracket }) => {
    const rounds = bracket.rounds || {};
    const roundNames = Object.keys(rounds);

    if (roundNames.length === 0) {
        return <div className="text-center text-muted-foreground">No matches scheduled</div>;
    }

    return (
        <div className="space-y-8">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Total Matches" value={bracket.total_matches || 0} icon={Trophy} />
                <StatCard label="Teams" value={bracket.teams?.length || 0} icon={Users} />
                <StatCard label="Rounds" value={roundNames.length} icon={Calendar} />
                <StatCard 
                    label="Status" 
                    value={bracket.is_complete ? "Complete" : "In Progress"} 
                    icon={bracket.is_complete ? Crown : Shield}
                    highlight={bracket.is_complete}
                />
            </div>

            {/* Bracket Visualization */}
            <div className="overflow-x-auto pb-4">
                <div className="flex gap-8 min-w-max">
                    {roundNames.map((roundName, roundIndex) => (
                        <div key={roundName} className="flex-shrink-0">
                            <div className="text-center mb-4">
                                <h3 className="font-heading font-bold text-lg uppercase text-primary">
                                    {roundName}
                                </h3>
                                <p className="text-xs text-muted-foreground">
                                    {rounds[roundName].length} {rounds[roundName].length === 1 ? 'match' : 'matches'}
                                </p>
                            </div>
                            <div className="space-y-4" style={{ 
                                marginTop: roundIndex > 0 ? `${Math.pow(2, roundIndex) * 2}rem` : 0 
                            }}>
                                {rounds[roundName].map((match, matchIndex) => (
                                    <MatchCard 
                                        key={match.id} 
                                        match={match} 
                                        isLast={roundIndex === roundNames.length - 1}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Match List View */}
            <div className="bg-[#0d2847] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-lg uppercase mb-4 text-white">All Matches</h3>
                <div className="space-y-2">
                    {bracket.matches?.map((match) => (
                        <div 
                            key={match.id} 
                            className="flex items-center justify-between p-3 bg-[#0a1628] border border-white/5"
                        >
                            <div className="flex items-center gap-4">
                                <span className="text-xs text-primary">{match.round}</span>
                                <span className="text-white">{match.team1}</span>
                                <span className="text-muted-foreground">vs</span>
                                <span className="text-white">{match.team2}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                {match.status === 'completed' && (
                                    <span className="text-lg font-bold text-primary">
                                        {match.score1} - {match.score2}
                                    </span>
                                )}
                                <span className={`text-xs px-2 py-1 ${
                                    match.status === 'live' ? 'bg-red-500/20 text-red-400' :
                                    match.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                    'bg-gray-500/20 text-gray-400'
                                }`}>
                                    {match.status}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const MatchCard = ({ match, isLast }) => {
    const isLive = match.status === 'live';
    const isCompleted = match.status === 'completed';

    return (
        <div 
            className={`w-64 bg-[#0d2847] border ${isLive ? 'border-red-500' : 'border-white/5'} relative`}
            data-testid={`bracket-match-${match.id}`}
        >
            {isLive && (
                <div className="absolute -top-2 left-2 text-xs bg-red-500 text-white px-2 py-0.5 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                    LIVE
                </div>
            )}
            
            {/* Team 1 */}
            <div className={`p-3 flex items-center justify-between border-b border-white/5 ${
                isCompleted && match.winner === match.team1 ? 'bg-primary/10' : ''
            }`}>
                <span className={`text-sm ${match.winner === match.team1 ? 'text-primary font-bold' : 'text-white'}`}>
                    {match.team1 || 'TBD'}
                </span>
                <span className="text-lg font-bold text-white">{match.score1 ?? '-'}</span>
            </div>
            
            {/* Team 2 */}
            <div className={`p-3 flex items-center justify-between ${
                isCompleted && match.winner === match.team2 ? 'bg-primary/10' : ''
            }`}>
                <span className={`text-sm ${match.winner === match.team2 ? 'text-primary font-bold' : 'text-white'}`}>
                    {match.team2 || 'TBD'}
                </span>
                <span className="text-lg font-bold text-white">{match.score2 ?? '-'}</span>
            </div>

            {/* Connector Line */}
            {!isLast && (
                <div className="absolute right-0 top-1/2 w-8 border-t border-white/20 transform translate-x-full" />
            )}
        </div>
    );
};

const StatCard = ({ label, value, icon: Icon, highlight }) => (
    <div className="bg-[#0d2847] border border-white/5 p-4">
        <Icon className={`w-5 h-5 ${highlight ? 'text-primary' : 'text-muted-foreground'} mb-2`} />
        <div className={`text-2xl font-bold ${highlight ? 'text-primary' : 'text-white'}`}>{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
    </div>
);

export default TournamentBracketPage;
